/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { authCallback } from "../funcs/authCallback.js";
import { authEnterDemo } from "../funcs/authEnterDemo.js";
import { authInfo } from "../funcs/authInfo.js";
import { authLogin } from "../funcs/authLogin.js";
import { authLogout } from "../funcs/authLogout.js";
import { authLogoutEverywhere } from "../funcs/authLogoutEverywhere.js";
import { authRegister } from "../funcs/authRegister.js";
import { authSwitchScopes } from "../funcs/authSwitchScopes.js";
import { authTransferIn } from "../funcs/authTransferIn.js";
import { authTransferOut } from "../funcs/authTransferOut.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import {
  AuthCallbackRequest,
  AuthCallbackResponse,
} from "../models/operations/authcallback.js";
import {
  AuthLoginRequest,
  AuthLoginResponse,
} from "../models/operations/authlogin.js";
import {
  AuthTransferInRequest,
  AuthTransferInResponse,
} from "../models/operations/authtransferin.js";
import {
  AuthTransferOutRequest,
  AuthTransferOutResponse,
} from "../models/operations/authtransferout.js";
import {
  EnterDemoRequest,
  EnterDemoResponse,
  EnterDemoSecurity,
} from "../models/operations/enterdemo.js";
import {
  LogoutRequest,
  LogoutResponse,
  LogoutSecurity,
} from "../models/operations/logout.js";
import {
  LogoutEverywhereRequest,
  LogoutEverywhereResponse,
  LogoutEverywhereSecurity,
} from "../models/operations/logouteverywhere.js";
import {
  RegisterRequest,
  RegisterSecurity,
} from "../models/operations/register.js";
import {
  SessionInfoRequest,
  SessionInfoResponse,
  SessionInfoSecurity,
} from "../models/operations/sessioninfo.js";
import {
  SwitchAuthScopesRequest,
  SwitchAuthScopesResponse,
  SwitchAuthScopesSecurity,
} from "../models/operations/switchauthscopes.js";
import { unwrapAsync } from "../types/fp.js";

export class Auth extends ClientSDK {
  /**
   * callback auth
   *
   * @remarks
   * Handles the authentication callback.
   */
  async callback(
    request: AuthCallbackRequest,
    options?: RequestOptions,
  ): Promise<AuthCallbackResponse | undefined> {
    return unwrapAsync(authCallback(
      this,
      request,
      options,
    ));
  }

  /**
   * enterDemo auth
   *
   * @remarks
   * Switches the current session into the shared read-only demo organization.
   */
  async enterDemo(
    request?: EnterDemoRequest | undefined,
    security?: EnterDemoSecurity | undefined,
    options?: RequestOptions,
  ): Promise<EnterDemoResponse | undefined> {
    return unwrapAsync(authEnterDemo(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * info auth
   *
   * @remarks
   * Provides information about the current authentication status.
   */
  async info(
    request?: SessionInfoRequest | undefined,
    security?: SessionInfoSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SessionInfoResponse> {
    return unwrapAsync(authInfo(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * login auth
   *
   * @remarks
   * Proxies to auth login through speakeasy oidc.
   */
  async login(
    request?: AuthLoginRequest | undefined,
    options?: RequestOptions,
  ): Promise<AuthLoginResponse | undefined> {
    return unwrapAsync(authLogin(
      this,
      request,
      options,
    ));
  }

  /**
   * logout auth
   *
   * @remarks
   * Logs out the current user by clearing their session.
   */
  async logout(
    request?: LogoutRequest | undefined,
    security?: LogoutSecurity | undefined,
    options?: RequestOptions,
  ): Promise<LogoutResponse | undefined> {
    return unwrapAsync(authLogout(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * logoutEverywhere auth
   *
   * @remarks
   * Signs the caller out everywhere: ends every dashboard and identity provider session, revokes their sign-ins in every organization, and revokes every MCP grant made from them.
   */
  async logoutEverywhere(
    request?: LogoutEverywhereRequest | undefined,
    security?: LogoutEverywhereSecurity | undefined,
    options?: RequestOptions,
  ): Promise<LogoutEverywhereResponse> {
    return unwrapAsync(authLogoutEverywhere(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * register auth
   *
   * @remarks
   * Register a new org for a user with their session information.
   */
  async register(
    request: RegisterRequest,
    security?: RegisterSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(authRegister(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * switchScopes auth
   *
   * @remarks
   * Switches the authentication scope to a different organization.
   */
  async switchScopes(
    request?: SwitchAuthScopesRequest | undefined,
    security?: SwitchAuthScopesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SwitchAuthScopesResponse | undefined> {
    return unwrapAsync(authSwitchScopes(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * transferIn auth
   *
   * @remarks
   * Steps 1/3 and 3/3 of a cross-domain session transfer, on the target platform host. Step 1/3 (start: source_host, no code) sets a short-lived cookie that binds the transfer to this browser and redirects to Step 2/3, transferOut on the source host. Step 3/3 (callback: code, no source_host) redeems the one-time code that transferOut issued, checks it against that cookie, and sets a new session cookie on this host. The cookie exists because a code alone would let anyone who holds one sign another person into the code's account (login CSRF). A request with both or neither, and any failed check, lands on this host's login page with a signin_error code; a failed callback never starts a new transfer. See the flow diagram in server/internal/auth/transfer.go.
   */
  async transferIn(
    request?: AuthTransferInRequest | undefined,
    options?: RequestOptions,
  ): Promise<AuthTransferInResponse | undefined> {
    return unwrapAsync(authTransferIn(
      this,
      request,
      options,
    ));
  }

  /**
   * transferOut auth
   *
   * @remarks
   * Step 2/3 (authorize) of a cross-domain session transfer, on the source platform host. Reached from Step 1/3, transferIn's start mode on the target host; redirects to Step 3/3, transferIn's callback mode there. Authenticates the session from the session cookie or header, checks that its active organization's default host is the target, and stores a one-time transfer code bound to the browser's nonce. Only an ordinary session whose organization lives on the target host can transfer. On any failure the browser is sent to a login page with a signin_error code instead of an error. See the flow diagram in server/internal/auth/transfer.go.
   */
  async transferOut(
    request?: AuthTransferOutRequest | undefined,
    options?: RequestOptions,
  ): Promise<AuthTransferOutResponse | undefined> {
    return unwrapAsync(authTransferOut(
      this,
      request,
      options,
    ));
  }
}
